import { useEffect, useState, useRef } from "react";
import { AppMode, getAppMode } from "types/app-mode";

const appMode = getAppMode();
const isModal = appMode === AppMode.Modal;
// Animation duration in ms
export const ANIMATION_DURATION = isModal ? 180 : 0;

/**
 * Slide animation direction options
 */
type SlideDirection = "right" | "bottom" | "left" | "top";

/**
 * Custom hook for managing slide modal animations
 *
 * @param isOpen - External state controlling if modal should be open
 * @param defaultDirection - Initial slide direction (default: "right")
 * @returns Object with animation state and controls
 */
function useSlideModal(isOpen: boolean, defaultDirection: SlideDirection = "right") {
    const [slideDirection, setCurrentSlideDirection] = useState<SlideDirection>(defaultDirection);
    const [isModalOpen, setIsModalOpen] = useState(isOpen);
    const [slideIn, setSlideIn] = useState(isOpen);

    // Track previous isOpen to detect transitions
    const prevIsOpenRef = useRef(isOpen);
    const desiredDirectionRef = useRef<SlideDirection>(defaultDirection);
    const slideInRef = useRef(slideIn);
    const exitAnimationFrameRef = useRef<number>();

    useEffect(() => {
        slideInRef.current = slideIn;
    }, [slideIn]);

    const setSlideDirection = (direction: SlideDirection) => {
        desiredDirectionRef.current = direction;

        if (!slideInRef.current) {
            setCurrentSlideDirection(direction);
        }
    };

    useEffect(() => {
        const wasOpen = prevIsOpenRef.current;
        const isOpening = isOpen && !wasOpen;
        const isClosing = !isOpen && wasOpen;

        if (isOpening) {
            setIsModalOpen(true);
            setSlideIn(false);

            desiredDirectionRef.current = defaultDirection;
            setCurrentSlideDirection(defaultDirection);

            // Trigger slide animation after a small delay to ensure Modal has rendered
            const slideTimer = setTimeout(() => {
                setSlideIn(true);
            }, 50);

            prevIsOpenRef.current = isOpen;
            return () => {
                clearTimeout(slideTimer);
            };
        } else if (isClosing) {
            setCurrentSlideDirection(desiredDirectionRef.current);

            const scheduleSlideOut = () => {
                exitAnimationFrameRef.current = undefined;
                setSlideIn(false);
            };

            if (typeof window !== "undefined") {
                exitAnimationFrameRef.current = window.requestAnimationFrame(scheduleSlideOut);
            } else {
                scheduleSlideOut();
            }

            const timer = setTimeout(() => {
                setIsModalOpen(false);

                desiredDirectionRef.current = defaultDirection;
                setCurrentSlideDirection(defaultDirection);
            }, ANIMATION_DURATION);

            prevIsOpenRef.current = isOpen;
            return () => {
                if (typeof window !== "undefined" && exitAnimationFrameRef.current) {
                    window.cancelAnimationFrame(exitAnimationFrameRef.current);
                    exitAnimationFrameRef.current = undefined;
                }

                clearTimeout(timer);
            };
        }

        prevIsOpenRef.current = isOpen;
    }, [isOpen, defaultDirection]);

    return {
        slideDirection,
        setSlideDirection,
        isModalOpen,
        slideIn,
        ANIMATION_DURATION,
    };
}

export { useSlideModal };
